import { Link } from 'react-router-dom';
import { mangaList } from '@/mocks/manga';

interface MangaSectionProps {
  title: string;
  subtitle?: string;
  mangaIds: number[];
  viewAllLink?: string;
  viewAllLabel?: string;
  badge?: string;
}

function MangaCard({ manga }: { manga: typeof mangaList[0] }) {
  return (
    <Link
      to={`/article/${manga.id}`}
      className="group block bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-300"
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-background-100">
        <img
          src={manga.cardImage}
          alt={manga.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${
            manga.status === '连载中'
              ? 'bg-primary-500 text-background-50'
              : 'bg-foreground-700 text-background-50'
          }`}>
            {manga.status}
          </span>
        </div>
        <div className="absolute top-2 right-2 flex items-center gap-1 bg-background-950/60 backdrop-blur-sm rounded-full px-2 py-0.5">
          <i className="ri-star-fill text-accent-500 text-[10px]"></i>
          <span className="text-background-50 text-[10px] font-medium">{manga.rating}</span>
        </div>
      </div>
      <div className="p-3 md:p-4">
        <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
          {manga.title}
        </h3>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="text-xs text-foreground-600">{manga.author}</span>
          <span className="text-foreground-300 text-[10px]">·</span>
          <span className="text-xs text-foreground-500">共{manga.chapters}话</span>
        </div>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {manga.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-800 rounded text-[10px] whitespace-nowrap">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}

export default function MangaSection({ title, subtitle, mangaIds, viewAllLink, viewAllLabel, badge }: MangaSectionProps) {
  const mangas = mangaIds.map(id => mangaList.find(m => m.id === id)).filter(Boolean) as typeof mangaList;

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14" aria-labelledby={`section-${title}`}>
      <div className="flex items-end justify-between mb-7">
        <div>
          <div className="flex items-center gap-3">
            <h2 id={`section-${title}`} className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
              {title}
            </h2>
            {badge && (
              <span className="px-2.5 py-0.5 bg-accent-100 text-accent-800 rounded-full text-xs font-medium whitespace-nowrap">
                {badge}
              </span>
            )}
          </div>
          {subtitle && (
            <p className="mt-1.5 text-sm text-foreground-600">{subtitle}</p>
          )}
        </div>
        {viewAllLink && (
          <Link
            to={viewAllLink}
            className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap"
          >
            {viewAllLabel || '查看全部'}
            <i className="ri-arrow-right-line"></i>
          </Link>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
        {mangas.map((manga) => (
          <MangaCard key={manga.id} manga={manga} />
        ))}
      </div>
    </section>
  );
}